import hotkeys from 'hotkeys-js';
import type {IHotkeyCallback, IHotkeyRecord, IHotkeysOptions, IRegisteredKuHotKey} from './typings';

/**
 * 快捷键管理类
 */
class HotKey {
    private hotkeysInstance: typeof hotkeys;
    private readonly registeredHotkeys: Map<string, IHotkeyRecord>;

    constructor(hotkeysInstance: typeof hotkeys) {
        this.hotkeysInstance = hotkeysInstance;
        this.registeredHotkeys = new Map();

        // 配置 hotkeys-js 全局选项
        this.hotkeysInstance.filter = (event: KeyboardEvent) => {
            return true;
        };
    }

    /**
     * 注册快捷键
     * @param hotkey 快捷键 key
     * @param callback 快捷键回调
     * @param options hotkeys-js 配置项
     */
    register(
        hotkey: string,
        callback: IHotkeyCallback,
        options: IHotkeysOptions = {}
    ): void {
        try {
            hotkey = this.hotkeyTrim(hotkey);
            // 验证快捷键格式是否合法
            const isValid = this.validateHotkey(hotkey);
            if (!isValid) {
                throw new Error(`非法快捷键格式: ${hotkey}`);
            }

            // 检查是否与已注册快捷键冲突
            const conflict = this.checkConflict(hotkey);
            if (conflict) {
                throw new Error(`检测到快捷键冲突: ${hotkey}`);
            }

            // 注册快捷键
            this.hotkeysInstance(hotkey, options, (event, handler) => {
                try {
                    callback(event, handler);
                } catch (error) {
                    console.error(`执行快捷键回调出错 (${hotkey}):`, error);
                }
            });

            // 存储原始回调
            this.registeredHotkeys.set(hotkey, {
                callback,
                options,
                timestamp: Date.now(),
            });

        } catch (error) {
            throw error;
        }
    }

    /**
     * 取消注册快捷键
     * @param hotkey 快捷键 key
     */
    unregister(hotkey: string): void {
        hotkey = this.hotkeyTrim(hotkey);
        try {
            this.hotkeysInstance.unbind(hotkey);
            this.registeredHotkeys.delete(hotkey);
        } catch (error) {
            throw error;
        }
    }

    /**
     * 验证快捷键格式
     * @param hotkey 快捷键 key
     */
    private validateHotkey(hotkey: string): boolean {
        if (typeof hotkey !== 'string') {
            return false;
        }

        // 检查是否包含非法字符
        const invalidChars = /[^a-zA-Z0-9+_\-=.,!@#$%^&*()[\]{};:'"/?\\| \s,]/;
        if (invalidChars.test(hotkey)) {
            return false;
        }

        // 分割多个快捷键组合
        const hotkeyCombos = hotkey.split(/\s*,\s*/);

        // 合法修饰键列表
        const validModifiers = ['ctrl', 'alt', 'shift', 'meta', 'command', 'option'];

        for (const combo of hotkeyCombos) {
            // 跳过空组合
            if (!combo.trim()) {
                continue;
            }

            // 分割按键
            const keys = combo.split('+');

            // 检查是否为空
            if (keys.length === 0) {
                return false;
            }

            // 检查修饰键和普通按键的组合
            const modifiers = keys.slice(0, -1).map((key) => key.trim());
            const lastKey = keys[keys.length - 1].trim();

            // 验证修饰键
            for (const mod of modifiers) {
                if (!validModifiers.includes(mod.toLowerCase())) {
                    return false;
                }
            }

            // 验证最后一个键（普通按键）
            if (modifiers.length > 0 && !lastKey) {
                return false; // 修饰键后必须有普通按键
            }

            // 检查是否有重复的修饰键
            const uniqueModifiers = new Set(modifiers.map(m => m.toLowerCase()));
            if (uniqueModifiers.size !== modifiers.length) {
                return false;
            }
        }

        return true;
    }

    /**
     * 获取所有已注册的快捷键
     */
    getRegisteredHotkeys(): IRegisteredKuHotKey[] {
        return Array.from(this.registeredHotkeys.entries()).map(([hotkey, data]) => ({
            hotkey,
            options: data.options,
            timestamp: data.timestamp,
        }));
    }

    /**
     * 销毁 SDK 实例
     */
    destroy(): void {
        this.registeredHotkeys.forEach((_, hotkey) => {
            this.hotkeysInstance.unbind(hotkey);
        });
        this.registeredHotkeys.clear();
        this.hotkeysInstance = null!;
    }

    /**
     * 去掉快捷键 key 的空格
     * @param hotkey 快捷键 key
     */
    hotkeyTrim(hotkey: string) {
        return hotkey.replace(/\s/g, '');
    }

    /**
     * 检查快捷键冲突
     * @param hotkey 快捷键 key
     */
    private checkConflict(hotkey: string): IHotkeyRecord | null {
        if (this.registeredHotkeys.has(hotkey)) {
            return this.registeredHotkeys.get(hotkey)!;
        }
        return null;
    }
}

// 创建单例实例
const hotKey = new HotKey(hotkeys);

export default hotKey;
export type {IHotkeyCallback, IHotkeyRecord, IHotkeysOptions} from './typings';
